import { Effect, Schema } from 'effect';

/**
 * Hand-written contract for `GET /api/v2/shop/search/products/facets`.
 *
 * The generated schema (`gen/rcprofi.ts` → `SearchFacets`) types every bucket as
 * an opaque `Record<string, number | string>`, throwing away the `{ key, label,
 * count }` field structure we actually consume. So we own the decoded shape here,
 * mirroring `suggestResult.ts`: plain-structural, JSON-LD `@`-metadata stripped by
 * `schemaBodyJsonClean` / ignored as excess, and every group defaulting to its
 * empty value so a partial payload never throws.
 *
 * A query that matches nothing still returns the envelope with empty groups; a
 * channel-less request omits `price`. Counts and the price range are in the
 * search-products units (price in **minor units**, e.g. 239000 = 2 390 Kč).
 */

// One selectable facet value: the `key` sent back to the search endpoint
// (brand UUID / taxon code / attribute value key), a human `label`, and the
// number of products that match it within the current result set.
export const SearchFacetBucket = Schema.Struct({
  key: Schema.String,
  label: Schema.String,
  count: Schema.Finite,
}).annotate({ identifier: 'SearchFacetBucket' });

export type SearchFacetBucket = Schema.Schema.Type<typeof SearchFacetBucket>;

const emptyBuckets: ReadonlyArray<SearchFacetBucket> = [];

// Optional on the wire, required after decode: an absent group decodes to `[]`.
// `withDecodingDefaultKey` makes the key optional on the Encoded side, so the
// bare array schema is piped (not pre-wrapped) and the decoded Type stays a
// required array — same pattern as `suggestResult.ts`'s `HitGroup`.
const BucketGroup = Schema.Array(SearchFacetBucket).pipe(
  Schema.withDecodingDefaultKey(Effect.succeed(emptyBuckets))
);

// Channel-scoped price range in minor units. Absent when the request carried no
// `channel=`; defaulted to `null` so consumers branch on "no price facet".
export const SearchPriceFacet = Schema.Struct({
  min: Schema.Finite,
  max: Schema.Finite,
}).annotate({ identifier: 'SearchPriceFacet' });

export type SearchPriceFacet = Schema.Schema.Type<typeof SearchPriceFacet>;

// One attribute value bucket. Unlike brand/taxon buckets, only `select` facets
// carry a separate `label`; `checkbox`/`integer`/`float`/`text` send
// `label: null` (the `key` is the display value). This boundary forwards the
// null faithfully — the facet→attribute adapter resolves the `label ?? key`
// fallback.
export const SearchAttributeValueBucket = Schema.Struct({
  key: Schema.String,
  label: Schema.NullOr(Schema.String),
  count: Schema.Finite,
}).annotate({ identifier: 'SearchAttributeValueBucket' });

export type SearchAttributeValueBucket = Schema.Schema.Type<typeof SearchAttributeValueBucket>;

const emptyAttributeValues: ReadonlyArray<SearchAttributeValueBucket> = [];

const AttributeValueGroup = Schema.Array(SearchAttributeValueBucket).pipe(
  Schema.withDecodingDefaultKey(Effect.succeed(emptyAttributeValues))
);

// One attribute facet: a code, its display `name`, the raw backend `type`
// (e.g. `select`, `checkbox` — narrowed by the rail's facet→attribute adapter),
// an optional `unit`, and the value buckets. `type` stays a bare string here
// because this boundary only forwards it; the adapter owns the type narrowing.
export const SearchAttributeFacet = Schema.Struct({
  code: Schema.String,
  name: Schema.NullOr(Schema.String),
  type: Schema.String,
  unit: Schema.NullOr(Schema.String),
  values: AttributeValueGroup,
}).annotate({ identifier: 'SearchAttributeFacet' });

export type SearchAttributeFacet = Schema.Schema.Type<typeof SearchAttributeFacet>;

const emptyAttributes: ReadonlyArray<SearchAttributeFacet> = [];

const AttributeGroup = Schema.Array(SearchAttributeFacet).pipe(
  Schema.withDecodingDefaultKey(Effect.succeed(emptyAttributes))
);

export const SearchFacets = Schema.Struct({
  totalItems: Schema.Finite.pipe(Schema.withDecodingDefaultKey(Effect.succeed(0))),
  brands: BucketGroup,
  taxons: BucketGroup,
  price: Schema.NullOr(SearchPriceFacet).pipe(Schema.withDecodingDefaultKey(Effect.succeed(null))),
  inStockCount: Schema.Finite.pipe(Schema.withDecodingDefaultKey(Effect.succeed(0))),
  attributes: AttributeGroup,
}).annotate({ identifier: 'SearchFacets' });

export type SearchFacets = Schema.Schema.Type<typeof SearchFacets>;
